import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import {
  ContextWorkbenchEditorSidebarPanel,
  ContextWorkbenchEditorTab,
  ContextWorkbenchFileId,
  ContextWorkbenchState,
  getContextWorkbenchInitialState,
} from "../../components/context-workbench/redux";
import { ContextWorkbenchAskThread } from "../../components/context-workbench/redux/contextWorkbenchAskThread";
import {
  InitMagicRulesEntityPayload,
  initMagicRulesEntity,
} from "../../components/context-workbench/redux/reducers";
import {
  InitPublishChangesetPayload,
  initPublishChangeset as initPublishChangesetImpl,
} from "../../components/context-workbench/redux/reducers/publishChangesetReducers";
import { editorTabMatchesFile } from "../../components/context-workbench/redux/utils/editorTab";
import {
  ResizeWorkbenchRegionPayload,
  ToggleWorkbenchRegionPayload,
  resizeWorkbenchRegion,
  toggleWorkbenchRegion,
} from "../../components/workbench/redux-reducers/regionReducers";
import {
  CloseWorkbenchTabPayload,
  FocusWorkbenchTabPayload,
  NewWorkbenchTabPayload,
  UpdateWorkbenchTabPayload,
  closeWorkbenchTab,
  focusWorkbenchTab,
  newWorkbenchTab,
  openInWorkbenchTab,
  updateWorkbenchTab,
} from "../../components/workbench/redux-reducers/tabReducers";
import {
  UpdateWorkbenchViewportPayload,
  updateWorkbenchViewport,
} from "../../components/workbench/redux-reducers/viewportReducers";
import { filterTabs } from "../../components/workbench/state";
import { ContextWorkbenchQueryParams } from "../../route/routes";
import { RootState } from "../store";

const contextWorkbenchSlice = createSlice({
  name: "contextWorkbench",
  initialState: getContextWorkbenchInitialState(),
  reducers: {
    unmount(state) {
      // @ts-expect-error -- temp ignore
      state.entities = getContextWorkbenchInitialState().entities;
    },
    // --- Editor tabs ---
    newEditorTab(
      state,
      action: PayloadAction<NewWorkbenchTabPayload<ContextWorkbenchEditorTab>>,
    ) {
      newWorkbenchTab(state.editor, action.payload);
    },
    focusEditorTab(state, action: PayloadAction<FocusWorkbenchTabPayload>) {
      focusWorkbenchTab(state.editor, action.payload);
    },
    closeEditorTab(state, action: PayloadAction<CloseWorkbenchTabPayload>) {
      closeWorkbenchTab(state.editor, action.payload);
    },
    updateEditorTab(
      state,
      action: PayloadAction<
        UpdateWorkbenchTabPayload<ContextWorkbenchEditorTab>
      >,
    ) {
      updateWorkbenchTab(state.editor, action.payload);
    },

    // --- Editor viewport ---
    updateEditorViewport(
      state,
      action: PayloadAction<UpdateWorkbenchViewportPayload>,
    ) {
      updateWorkbenchViewport(state.editor, action.payload);
    },

    // --- Editor regions ---
    resizeEditorRegion(
      state,
      action: PayloadAction<ResizeWorkbenchRegionPayload>,
    ) {
      resizeWorkbenchRegion(state.editor.regions, action.payload);
    },
    toggleEditorRegion(
      state,
      action: PayloadAction<ToggleWorkbenchRegionPayload>,
    ) {
      toggleWorkbenchRegion(state.editor.regions, action.payload);
    },

    // --- Editor sidebar ---
    setEditorSidebarPanel(
      state,
      action: PayloadAction<{
        newPanelId: ContextWorkbenchEditorSidebarPanel.Id | null;
      }>,
    ) {
      state.editor.regions.left.isOpen = action.payload.newPanelId != null;
      state.editor.sidebar.panelId = action.payload.newPanelId;
    },

    // --- Editor files ---
    openEditorFileInTab(
      state,
      action: PayloadAction<{
        fileId: ContextWorkbenchFileId;
        tabType: ContextWorkbenchEditorTab.Type;
        isPreview?: boolean;
      }>,
    ) {
      openInWorkbenchTab<
        ContextWorkbenchState["editor"],
        ContextWorkbenchEditorTab
      >(
        state.editor,
        {
          fileId: action.payload.fileId,
          type: action.payload.tabType,
        },
        {
          getNewTabProperties: (payload) => ({
            ...payload,
            isPreview: action.payload.isPreview,
          }),
        },
      );
    },

    closeEditorFileTabs(
      state,
      action: PayloadAction<{
        fileId: ContextWorkbenchFileId;
      }>,
    ) {
      const tabs = filterTabs(
        state.editor.tabGroups,
        editorTabMatchesFile(action.payload.fileId),
      );
      for (const tab of tabs) {
        closeWorkbenchTab(state.editor, { tabId: tab.id });
      }
    },

    swapEditorFileTabs(
      state,
      action: PayloadAction<{
        fileIdBefore: ContextWorkbenchFileId;
        fileIdAfter: ContextWorkbenchFileId;
      }>,
    ) {
      const tabs = filterTabs(
        state.editor.tabGroups,
        editorTabMatchesFile(action.payload.fileIdBefore),
      );
      for (const tab of tabs) {
        updateWorkbenchTab<
          ContextWorkbenchState["editor"],
          ContextWorkbenchEditorTab.FileEditor
        >(state.editor, {
          tabId: tab.id,
          producerFn: (draftTab) => {
            draftTab.fileId = ContextWorkbenchFileId.check(
              action.payload.fileIdAfter,
            );
          },
        });
      }
    },

    // --- Entities ---
    initMagicRulesEntity(
      state,
      action: PayloadAction<InitMagicRulesEntityPayload>,
    ) {
      initMagicRulesEntity(state, action.payload);
    },

    // --- Publish ---
    initPublishChangeset(
      state,
      action: PayloadAction<InitPublishChangesetPayload>,
    ) {
      initPublishChangesetImpl(state, action.payload);
    },

    setPublishSelectedFileId(
      state,
      action: PayloadAction<ContextWorkbenchFileId | null>,
    ) {
      state.publish.selectedFileId = action.payload;
    },

    setPublishAskPreviewThread(
      state,
      action: PayloadAction<ContextWorkbenchAskThread | null>,
    ) {
      state.publish.askPreviewThread = action.payload;
    },

    resetPublishState(state) {
      state.publish.askPreviewThread = null;
      state.publish.selectedFileId = null;
    },

    syncPublishStateFromRouteQueryParams(
      state,
      action: PayloadAction<{
        queryParams: {
          publishView: ContextWorkbenchQueryParams.PublishView | null;
          publishChangesetId: ContextWorkbenchQueryParams.PublishChangesetId | null;
        };
      }>,
    ) {
      const { publishChangesetId, publishView } = action.payload.queryParams;
      if (publishView == null) {
        state.view = "editor";
      } else {
        state.view = "publish";
        state.publish.view = publishView;
      }
      state.publish.changeset.loadId = publishChangesetId ?? undefined;
    },
  },
});

export const contextWorkbenchReducer = contextWorkbenchSlice.reducer;

export const contextWorkbenchActions = contextWorkbenchSlice.actions;

// *** Selectors **************************************************************
export function selectContextWorkbench(
  state: Pick<RootState, "contextWorkbench">,
): ContextWorkbenchState {
  return state.contextWorkbench;
}
